import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import {
  describe,
  PropTypes,
  useMemoMergePropsWithDefault,
  useValidateTypescriptPropTypes,
} from '@elliemae/ds-props-helpers';
import {
  addSpecialCharacters,
  getPartialMaskedPos,
  setCursorPosition,
  useCallbackAfterRender,
} from '../utils/index.js';
import { useMaskDefaultProps, useMaskPropTypes, type UseMaskT } from '../react-desc-prop-types.js';
import { useZipCodeMaskName } from '../DSMaskDefinitions.js';

const zipCodeSpecialChars: [string, number][] = [['-', 5]];

const conformValue = (rawValue: string, cursorPos: number, lastKeyCode: string) => {
  // Work with digits only
  let maskedValue = String(rawValue)
    .split('')
    .filter((char) => char >= '0' && char <= '9');

  // We only consider first 9 digits
  maskedValue = maskedValue.slice(0, 9);

  const maskedPos = getPartialMaskedPos(rawValue, maskedValue, cursorPos);

  return addSpecialCharacters(maskedValue, zipCodeSpecialChars, maskedPos, lastKeyCode);
};

export const useZipCodeMask: ((args: UseMaskT.Props) => UseMaskT.OutputT) & { displayName: string } = (props) => {
  const propsWithDefault = useMemoMergePropsWithDefault<UseMaskT.InternalProps>(props, useMaskDefaultProps);
  useValidateTypescriptPropTypes(propsWithDefault, useMaskPropTypes, useZipCodeMaskName);
  const { valueSetter, onChange: userOnChange, onKeyDown: userOnKeyDown } = propsWithDefault;

  const lastKeyCode = useRef('Unidentified');
  const scheduleAfterRender = useCallbackAfterRender(true);

  // Some times, the value of the mask is not gonna change (user types invalid char for example)
  // This makes the input to go from "correct" -> "incorrect" -> "correct"
  // But since the input is controlled, the input does not re-render
  // This makes the input go mumbo-jumbo and places the cursor in the end, because we kinda changed the value async
  // Solution for this is to trigger a re-render, we do this with a set state call in the on change event
  const [, setKey] = useState(0);

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      lastKeyCode.current = e.code;
      if (userOnKeyDown) userOnKeyDown(e);
    },
    [userOnKeyDown],
  );

  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const [mask, cursorPos] = conformValue(e.target.value, e.target.selectionEnd ?? 0, lastKeyCode.current);
      valueSetter((prevMask) => {
        // Here we need to update the key
        if (mask === prevMask) setKey((key) => key + 1);

        return mask;
      });
      if (userOnChange) userOnChange(e, mask);
      scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
    },
    [valueSetter, scheduleAfterRender, userOnChange],
  );

  return {
    onKeyDown,
    onChange,
  };
};

export const getZipCodeMaskedValue = (value: string) => {
  const [maskedValue] = conformValue(value, value.length - 1, '');
  return maskedValue;
};

useZipCodeMask.displayName = useZipCodeMaskName;
export const UseZipCodeMaskWithSchema = describe(useZipCodeMask);
UseZipCodeMaskWithSchema.propTypes = useMaskPropTypes;
UseZipCodeMaskWithSchema.returnType = {
  onKeyDown: PropTypes.func.description('On key down event for input').isRequired,
  onChange: PropTypes.func.description('On change event for input').isRequired,
};
